import axios, { CancelTokenSource } from 'axios';

import { fetchListings as requestListings } from '../../../../utils/listingsTransport';
import { prepareSearchParams } from '../../../../utils/criteriaSearchParams';

/**
 * Service for fetching listings based on criteria from the shared form
 */

interface ListingsCriteriaParams {
  [key: string]: any;
}

// Keep track of cancel token for request cancellation
let cancelTokenSource: CancelTokenSource | null = null;


/**
 * Fetches the count of listings matching the given criteria
 * Cancels any previous pending request automatically
 */
export async function fetchListingsCount(
  formData: ListingsCriteriaParams,
  onProgress?: (isLoading: boolean) => void
): Promise<number> {
  // Cancel previous request if exists
  if (cancelTokenSource) {
    cancelTokenSource.cancel('Operation canceled due to new request.');
  }

  // Create new cancel token
  cancelTokenSource = axios.CancelToken.source();

  try {
    onProgress?.(true);

    const searchParams = prepareSearchParams(formData);

    const data = await requestListings({
      method: 'doCriteriaSearch',
      // Only TOTALROWCOUNT is read here, so skip the detail columns.
      params: { ...searchParams, IncludeDetailFields: false },
      cancelToken: cancelTokenSource.token,
    });

    if (data.STATUS === 0) {
      const count = data.TOTALROWCOUNT || 0;
      return count;
    } else {
      console.error('❌ Listings API error:', data.MESSAGE);
      return 0;
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return 0;
    } else {
      console.error('Error fetching listings count:', error);
      return 0;
    }
  } finally {
    onProgress?.(false);
  }
}

/**
 * Fetches full listings data matching the given criteria
 * Returns both the count and the listing records
 */
export async function fetchListings(
  formData: ListingsCriteriaParams,
  onProgress?: (isLoading: boolean) => void
): Promise<{ count: number; listings: any[] }> {
  // Cancel previous request if exists
  if (cancelTokenSource) {
    cancelTokenSource.cancel('Operation canceled due to new request.');
  }

  // Create new cancel token
  cancelTokenSource = axios.CancelToken.source();

  try {
    onProgress?.(true);

    const searchParams = prepareSearchParams(formData);

    const data = await requestListings({
      method: 'doCriteriaSearch',
      params: searchParams,
      cancelToken: cancelTokenSource.token,
    });

    if (data.STATUS === 0) {
      return {
        count: data.TOTALROWCOUNT || 0,
        listings: data.SEARCHLISTINGS || [],
      };
    } else {
      console.error('Listings API error:', data.MESSAGE);
      return { count: 0, listings: [] };
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      return { count: 0, listings: [] };
    } else {
      console.error('Error fetching listings:', error);
      return { count: 0, listings: [] };
    }
  } finally {
    onProgress?.(false);
  }
}

/**
 * Cancels any pending listings request
 */
export function cancelListingsRequest(): void {
  if (cancelTokenSource) {
    cancelTokenSource.cancel('Request manually canceled');
    cancelTokenSource = null;
  }
}
